import Role, { MODEL_STR } from "byzantine/src/Role";
import { ContextForm, useFormData, useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { TextInput, Checkbox } from "@narmi/design_system";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Account from "byzantine/src/Account";
import { useRolesContext } from "byzantine/src/Role/RolesProvider";
import OrganizationUser from "byzantine/src/OrganizationUser";
import utils from "../../utils";
import BaseBanner from "../BaseBanner";
import OrgRoleActions from "./OrgRoleActions";
import OrganizationAccessLevelExtraPermissions from "./OrganizationAccessLevelExtraPermissions";
import AccountCheckList from "../AccountCheckList";
import SectionCard from "../SectionCard";
import { validateExtraPermissions } from "./OrganizationEditUser";
import {
  validateAccounts,
  getDefaultLimits,
  buildInitialFormData,
  buildRoleFromFormData,
} from "./RoleFormUtils";

const NON_EDITABLE_ROLE_NAMES = new Set([
  OrganizationUser.ROLE.ADMIN,
  OrganizationUser.ROLE.COLLABORATOR,
  OrganizationUser.ROLE.VIEWER,
  OrganizationUser.ROLE.ACCOUNT_HOLDER,
  "No Permissions",
]);

interface OrganizationEditRoleProps {
  accounts?: Account[];
  externalAccounts?: Account[];
  orgId: string;
  roleUuid?: string;
  features?: Record<string, any>;
  organizationUsers: any[];
  isUsingConsumerBillpayJson?: string;
  [key: string]: any;
}

interface OrganizationEditRoleFormProps extends OrganizationEditRoleProps {
  roleToEdit: Role;
}

function OrganizationEditRoleForm({
  accounts = [],
  externalAccounts = [],
  roleToEdit,
  ...props
}: OrganizationEditRoleFormProps) {
  const { updateRole } = useRolesContext();
  const { roles } = useRolesContext();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const isUsingConsumerBillpay = !!JSON.parse(
    props.isUsingConsumerBillpayJson || "false"
  );
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const ContextFormComponent = ContextForm as any;
  const CheckboxComponent = Checkbox as any;
  const AccountCheckListComponent = AccountCheckList as any;
  const BaseBannerComponent = BaseBanner as any;

  const defaultLimits = getDefaultLimits(props.features);

  const initialData = buildInitialFormData({
    role: roleToEdit,
    accounts,
    externalAccounts,
    defaultLimits,
  });

  const { onChange, formData } = useFormData(initialData);

  // uncheck "All accounts" box if any internal account is unchecked
  useEffect(() => {
    const update: Record<string, any> = {};
    if (!utils.allAreSelected(formData.accounts)) {
      update[MODEL_STR.ACCOUNT] = false;
    }
    if (Object.keys(update).length > 0) {
      onChange(update);
    }
  }, [formData.accounts]);

  // check all internal account when "All accounts" is selected
  useEffect(() => {
    if (formData[MODEL_STR.ACCOUNT]) {
      const newAccounts = formData.accounts.map((account: any) => {
        const newAccount = new Account(account);
        if (!(newAccount as any).selected) {
          (newAccount as any).selected = !(newAccount as any).selected;
        }
        return newAccount;
      });
      onChange({ accounts: newAccounts });
    }
  }, [formData[MODEL_STR.ACCOUNT]]);

  const onSubmit = async (callback: () => void) => {
    const validationError = validateExtraPermissions(formData);

    if (validationError) {
      setPermissionsError(validationError);
      callback();
      return;
    }

    try {
      setIsCreating(true);
      const { role: generatedRole } = buildRoleFromFormData(formData, roles);

      const updatedRole = new Role({
        uuid: (roleUuid || props.roleUuid) as string,
        name: formData.role_name,
        description: formData.role_description,
        permissions: generatedRole.permissions,
        limits: generatedRole.limits,
        organization_uuid: props.orgId,
      });

      await updateRole(updatedRole.serialize() as any);
      sendNotification({
        type: "success",
        text: l10n.getString("org-role-update-success"),
      });
      // Wait for toast to render before redirecting
      setTimeout(() => {
        callback();
        navigate(`/roles/${roleUuid || props.roleUuid}`);
      }, 1000);
    } catch (apiError) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-update-error"),
      });
      setIsCreating(false);
      callback();
    }
  };

  return (
    <div style={{ minHeight: "800px" }}>
      <BaseBannerComponent
        bannerName={l10n.getString("org-role-edit-title")}
        goBack={() => {
          navigate("/roles");
        }}
      />
      <p>{l10n.getString("org-role-edit-description")}</p>
      <ContextFormComponent data={formData} onChange={onChange}>
        <div className="section-box">
          <div className="nds-grid nds-span-12">
            <div className="nds-span-6 nds-span-mobile-12 nds-input-style-override">
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                {l10n.getString("org-role-field-name")}
              </label>
              <ContextForm.Field required style={{ marginBottom: "0px" }}>
                <TextInput
                  id="role-name"
                  field="role_name"
                />
              </ContextForm.Field>
            </div>
            <div className="nds-span-6 nds-span-mobile-12 nds-input-style-override">
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                {l10n.getString("org-role-field-description")}
              </label>
              <ContextForm.Field style={{ marginBottom: "0px" }}>
                <TextInput
                  id="role-description"
                  field="role_description"
                />
              </ContextForm.Field>
            </div>
          </div>
        </div>
        <div className="section-box">
          <span className="section-title section-border nds-span-1">
            {l10n.getString("org-role-section-access")}
          </span>
          <SectionCard isLoading={false} paddingSize={null}>
            <div className="padding--x--l padding--top--l">
              <SectionCard.Title text={l10n.getString("org-role-section-accounts")} />
            </div>
            <div className="permission-check-list">
              <div
                className="inverted-primary-color-checkbox"
                style={{ borderBottom: "1px solid RGBA(var(--border-color))" }}
              >
                <ContextForm.Field style={{ marginBottom: "0px" }}>
                  <CheckboxComponent
                    field={MODEL_STR.ACCOUNT}
                    label={l10n.getString("org-role-all-accounts-checkbox")}
                  />
                </ContextForm.Field>
              </div>
               <ContextForm.Field
                 required
                 validate={validateAccounts}
                 style={{ marginBottom: "0px" }}
               >
                 <AccountCheckListComponent
                   field="accounts"
                   onChange={onChange}
                   formData={formData}
                 />
               </ContextForm.Field>
            </div>
            <div className="permission-check-list">
              <OrganizationAccessLevelExtraPermissions
                formData={formData}
                onChange={onChange}
                features={props.features}
                isUsingConsumerBillpay={isUsingConsumerBillpay}
                error={permissionsError}
                organizationUsers={props.organizationUsers}
              />
            </div>
          </SectionCard>
        </div>
        <OrgRoleActions
          onSubmit={onSubmit}
          dangerouslyDisableShowLoading
          isDisabled={isCreating}
          onCancel={() => navigate("/roles")}
        />
      </ContextFormComponent>
    </div>
  );
}

function OrganizationEditRoleContent(props: OrganizationEditRoleProps) {
  const { roles, isLoading, error } = useRolesContext();
  const { roleUuid } = useParams<{ roleUuid?: string }>();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();
  const navigate = useNavigate();

  const roleToEdit = roles.find((role: Role) => role.uuid === (roleUuid || props.roleUuid));

  useEffect(() => {
    if (!roleUuid) {
      navigate("/roles", { replace: true });
      return;
    }

    if (isLoading || error) {
      return;
    }

    if (!roleToEdit) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate("/roles", { replace: true });
      return;
    }

    if (NON_EDITABLE_ROLE_NAMES.has(roleToEdit.name)) {
      sendNotification({
        type: "negative",
        text: l10n.getString("org-role-error-toast"),
      });
      navigate(`/roles/${roleToEdit.uuid}`, { replace: true });
    }
  }, [error, isLoading, l10n, navigate, roleToEdit, roleUuid, sendNotification]);

  if (isLoading) {
    return <div>{l10n.getString("org-role-loading")}</div>;
  }

  if (error) {
    return <div>{`${l10n.getString("org-role-error-toast")}: ${error.message}`}</div>;
  }

  if (!roleToEdit) {
    return null;
  }

  if (NON_EDITABLE_ROLE_NAMES.has(roleToEdit.name)) {
    return null;
  }

  return <OrganizationEditRoleForm {...props} roleToEdit={roleToEdit} />;
}

export default function OrganizationEditRole(
  props: OrganizationEditRoleProps
): any {
  return <OrganizationEditRoleContent {...props} />;
}
